import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';
import type { OrganizationSummary, Role } from '@/api/orgs';
import { useAuth } from './useAuth';

export interface OrgState {
  orgs: OrganizationSummary[];
  current: OrganizationSummary | null;
  currentRole: Role | null;
  select: (publicId: string) => void;
}

export const OrgContext = createContext<OrgState | null>(null);

/** Derives the org list and the selected org from `me.memberships`; selection persists in localStorage. */
export function OrgProvider({ children }: { children: ReactNode }) {
  const { me } = useAuth();
  const orgs = useMemo(() => me?.memberships.map((m) => m.organization) ?? [], [me]);
  const [currentId, setCurrentId] = useState<string | null>(() => localStorage.getItem('org'));
  const current = orgs.find((o) => o.public_id === currentId) ?? orgs[0] ?? null;
  const currentRole = me?.memberships.find((m) => m.organization.public_id === current?.public_id)?.role ?? null;
  const select = useCallback((id: string) => {
    localStorage.setItem('org', id);
    setCurrentId(id);
  }, []);
  const value = useMemo(() => ({ orgs, current, currentRole, select }), [orgs, current, currentRole, select]);
  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>;
}

/** Reads the org context; throws if used outside an OrgProvider. */
export function useOrg(): OrgState {
  const ctx = useContext(OrgContext);
  if (!ctx) throw new Error('useOrg must be used within an OrgProvider');
  return ctx;
}
